import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { useParams, Link } from "react-router-dom";
import {
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import { Icon } from "@iconify/react";
import classnames from "classnames";
import { getJobs } from "../../../../api/jobs";
import { apiErrorHandler } from "../../../../helpers/common";
import Selected from "../../../../helpers/Common/Selected";
import SmallLoader from "../../../../helpers/Common/SmallLoader";
import JobItemRow from "./JobItemRow";
import NoJobsPage from "./NoJobsPage";
import Section from "./Section";
import axios from "axios";

const ManageJobs = () => {
  const employer = useSelector((state) => state.employer);
  const jobLoading = employer.loading;
  const [loading, setLoading] = useState(false);
  const { companyId, companyName } = useParams();
  const [jobs, setJobs] = useState([]);
  const customer = useSelector((state) => state.customer.profile);
  const [activeTab, setActiveTab] = useState("1");
  const [isPlanActive, setIsPlanActive] = useState(false);


  // Updated state initialization to capture maxJobsAllowed
  const [dashboardCounts, setDashboardCounts] = useState({
    jobsPosted: 0,
    jobsRemaining: 0,
    maxJobsAllowed: 0,
  });
  const shouldBuyPlan =
    !isPlanActive || dashboardCounts.jobsRemaining <= 0;
  const token = localStorage.getItem("webvi_authToken");

  useEffect(() => {
    if (!customer?.id) return;

    const getEmployerDashboard = async () => {
      try {
        const res = await axios.get(
          `https://sdsjobseekers.runasp.net/api/Dashboard/employer`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setIsPlanActive(res.data?.data?.isPlanActive || false);

        // 🎯 Safely capturing all 3 key parameters here
        setDashboardCounts({
          jobsPosted: res.data?.data?.allJobsPosted || 0,
          jobsRemaining: res.data?.data?.remainingJobPosts || 0,
          maxJobsAllowed: res.data?.data?.maxJobsAllowed || 0,
        });
      } catch (err) {
        console.log(err);
        setIsPlanActive(false);
      }
    };

    getEmployerDashboard();
  }, [customer?.id, token]);

  useEffect(() => {
    if (companyId) {
      setLoading(true);
      const params = `&JobsSearch[employer_id]=${customer.id}&JobsSearch[job_company_id]=${companyId}`;
      getJobs(params)
        .then((res) => {
          if (res.status === 200) {
            setJobs(res.data.data);
          }
        })
        .catch((err) => {
          console.log("err::", err);
          apiErrorHandler(err);
        })
        .finally(() => setLoading(false));
    } else {
      setJobs(employer?.jobs.data);
    }
  }, [companyId, customer.id, employer?.jobs]);

  const activeJobs = Array.isArray(jobs)
    ? jobs.filter((job) => job?.isActive === 1)
    : [];

  const disabledJobs = Array.isArray(jobs)
    ? jobs.filter((job) => job?.isActive === 0)
    : [];

  return (
    <React.Fragment>
      <Helmet>
        <title>Manage Jobs | Webvi </title>
      </Helmet>

      <Section
        title={companyId && companyName}
        currentPageName={companyId && "Opening Jobs"}
      />

      {(jobLoading || loading) && <SmallLoader />}

      <section
        className="section"
        style={{ display: jobLoading || loading ? "none" : "block" }}
      >
        <Container>
          {jobs && jobs.length > 0 ? (
            <>
              {/* 📊 Premium 3-Column Counter Metrics Dashboard Container */}
              <Row className="justify-content-center mt-4 mb-4">
                <Col lg={10}>
                  <div className="bg-white rounded-4 border p-4 shadow-sm">
                    <Row className="align-items-center g-0">
                      {/* Column 1: Max Jobs Allowed Limit */}
                      <Col
                        xs={4}
                        className="pe-3 d-flex align-items-center gap-3"
                      >
                        <div className="bg-light p-3 rounded-3 text-secondary d-none d-sm-block">
                          <Icon
                            icon="material-symbols:assignment-sharp"
                            fontSize={30}
                          />
                        </div>
                        <div>
                          <span className="text-muted d-block small fw-bold text-uppercase tracking-wider mb-1">
                            Jobs Alloted
                          </span>
                          <h2 className="fw-extrabold text-dark mb-0 display-6">
                            {dashboardCounts.maxJobsAllowed}
                          </h2>
                        </div>
                      </Col>

                    
                      <Col
                        xs={4}
                        className="px-3 border-start border-light-subtle d-flex align-items-center gap-3"
                      >
                        <div className="bg-light p-3 rounded-3 text-dark d-none d-sm-block">
                          <Icon
                            icon="material-symbols:work-history-outline-rounded"
                            fontSize={30}
                          />
                        </div>
                        <div>
                          <span className="text-muted d-block small fw-bold text-uppercase tracking-wider mb-1">
                            Jobs Posted
                          </span>
                          <h2 className="fw-extrabold text-dark mb-0 display-6">
                            {dashboardCounts.jobsPosted}
                          </h2>
                        </div>
                      </Col>

                      {/* Column 3: Remaining Allocation Slots Left */}
                      <Col
                        xs={4}
                        className="ps-4 border-start border-light-subtle d-flex align-items-center gap-3"
                      >
                        <div
                          className={`p-3 rounded-3 d-none d-sm-block ${dashboardCounts.jobsRemaining <= 0
                            ? "bg-danger-subtle text-danger"
                            : "bg-success-subtle text-success"
                            }`}
                        >
                          <Icon
                            icon="material-symbols:assignment-turned-in-outline-rounded"
                            fontSize={30}
                          />
                        </div>
                        <div>
                          <span className="text-muted d-block small fw-bold text-uppercase tracking-wider mb-1">
                            Remaining Jobs
                          </span>
                          <h2
                            className={`fw-extrabold mb-0 display-6 ${dashboardCounts.jobsRemaining <= 0
                              ? "text-danger"
                              : "text-success"
                              }`}
                          >
                            {dashboardCounts.jobsRemaining}
                          </h2>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>

              <Selected
                itemData={jobs}
                buttonText={shouldBuyPlan ? "For Post a Job Buy a Plan" : "Add A New Job"}
                buttonUrl={
                  shouldBuyPlan
                    ? "/pricing"
                    : companyId
                      ? `/employer/job/add/${companyId}/${companyName}`
                      : "/employer/job/add"
                }
                disabled={false}
              />
              <Row className="justify-content-center mt-2">
                <Col lg={8}>
                  <Nav
                    className="job-list-menu nav-pills nav-justified flex-column flex-sm-row mb-4"
                    role="tablist"
                  >
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "1" })}
                        onClick={() => setActiveTab("1")}
                        type="button"
                        role="tab"
                      >
                        Active Jobs ({activeJobs?.length || 0})
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "2" })}
                        onClick={() => setActiveTab("2")}
                        type="button"
                        role="tab"
                      >
                        Disabled Jobs ({disabledJobs?.length || 0})
                      </NavLink>
                    </NavItem>
                  </Nav>
                </Col>
              </Row>

              <Row>
                <Col lg={12}>
                  <TabContent activeTab={activeTab}>
                    <TabPane tabId="1">
                      {activeJobs.map((job) => (
                        <Link
                          to={`/job-detail/${job.id}`}
                          key={job.id}
                          className="text-decoration-none text-unset d-block"
                          style={{ color: "inherit" }}
                        >
                          <JobItemRow jobDetails={job} />
                        </Link>
                      ))}

                      {activeJobs.length === 0 && (
                        <p className="text-center">No active jobs found.</p>
                      )}
                    </TabPane>
                    <TabPane tabId="2">
                      {disabledJobs.map((job) => (
                        <Link
                          to={`/job-detail/${job.id}`}
                          key={job.id}
                          className="text-decoration-none text-unset d-block"
                          style={{ color: "inherit" }}
                        >
                          <JobItemRow jobDetails={job} />
                        </Link>
                      ))}
                      {disabledJobs.length === 0 && (
                        <p className="text-center">No disabled jobs found.</p>
                      )}
                    </TabPane>
                  </TabContent>
                </Col>
              </Row>
            </>
          ) : (
            <NoJobsPage
              message="You haven't posted any jobs yet. Create your first job posting and hire for free!"
              buttonUrl={
                companyId
                  ? `/employer/job/add/${companyId}/${companyName}`
                  : "/employer/job/add"
              }
            />
          )}
        </Container>
      </section>
    </React.Fragment>
  );
};

export default ManageJobs;
